/* ============================================================
   Apipet Custom Mega Menu — behaviour
   <apipet-nav data-trigger="hover|click" data-delay="120">
   Handles: open/close (hover-intent or click), category tab
   switching, keyboard accessibility, outside-click + Escape.
   ============================================================ */
(function () {
  if (customElements.get('apipet-nav')) return;

  class ApipetNav extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.dataset.trigger === 'click' ? 'click' : 'hover';
      this.delay = parseInt(this.dataset.delay, 10) || 120;
      this.items = Array.from(this.querySelectorAll('.apipet-nav__item.has-panel'));
      this._closeTimer = null;
      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.items.forEach((item) => this._bindItem(item));
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      this._onReposition = () => {
        const open = this.querySelector('.apipet-nav__item.is-open');
        if (open) this._positionPanel(open);
      };
      window.addEventListener('scroll', this._onReposition, { passive: true });
      window.addEventListener('resize', this._onReposition);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('scroll', this._onReposition);
      window.removeEventListener('resize', this._onReposition);
    }

    _positionPanel(item) {
      const panel = item.querySelector('.apipet-nav__panel--full');
      if (!panel) return;
      const bar = this.querySelector('.apipet-nav__bar');
      if (!bar) return;
      panel.style.top = Math.round(bar.getBoundingClientRect().bottom) + 'px';
    }

    _bindItem(item) {
      const top = item.querySelector('.apipet-nav__top');
      const panel = item.querySelector('.apipet-nav__panel');
      if (!top || !panel) return;

      if (this.trigger === 'hover') {
        item.addEventListener('mouseenter', () => this._open(item));
        item.addEventListener('mouseleave', () => this._scheduleClose(item));
        // Still allow keyboard/click users to toggle
        top.addEventListener('click', (e) => {
          e.preventDefault();
          item.classList.contains('is-open') ? this._close(item) : this._open(item);
        });
      } else {
        top.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = item.classList.contains('is-open');
          this._closeAll();
          if (!isOpen) this._open(item);
        });
      }

      // Focus management for keyboard users
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) this._close(item);
      });

      // Category tab switching (left rail)
      const rail = item.querySelectorAll('.apipet-nav__rail-item[data-target]');
      rail.forEach((btn) => {
        const activate = () => this._activateCat(item, btn.dataset.target);
        btn.addEventListener('mouseenter', activate);
        btn.addEventListener('focus', activate);
        btn.addEventListener('click', (e) => {
          // If the rail item is a plain link with cards, let hover show cards
          // but allow navigation on click when it has an href.
          if (btn.tagName === 'BUTTON') e.preventDefault();
          activate();
        });
      });
    }

    _activateCat(item, targetId) {
      if (!targetId) return;
      item.querySelectorAll('.apipet-nav__rail-item[data-target]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.target === targetId);
      });
      item.querySelectorAll('.apipet-nav__cat').forEach((c) => {
        c.classList.toggle('is-active', c.id === targetId);
      });
    }

    _open(item) {
      clearTimeout(this._closeTimer);
      this.items.forEach((other) => {
        if (other !== item) this._close(other);
      });
      item.classList.add('is-open');
      this._positionPanel(item);
      const top = item.querySelector('.apipet-nav__top');
      if (top) top.setAttribute('aria-expanded', 'true');
    }

    _scheduleClose(item) {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => this._close(item), this.delay);
    }

    _close(item) {
      if (!item) return;
      item.classList.remove('is-open');
      const top = item.querySelector('.apipet-nav__top');
      if (top) top.setAttribute('aria-expanded', 'false');
    }

    _closeAll() {
      this.items.forEach((item) => this._close(item));
    }

    _onDocClick(e) {
      if (!this.contains(e.target)) this._closeAll();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        const open = this.querySelector('.apipet-nav__item.is-open');
        if (open) {
          this._close(open);
          const top = open.querySelector('.apipet-nav__top');
          if (top) top.focus();
        }
      }
    }
  }

  customElements.define('apipet-nav', ApipetNav);
})();
